import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";
import { API, WS_BASE } from '../config';

const AlertContext = createContext(null);

const SEVERITY_STYLES = {
  CRITICAL: { bg: "bg-destructive/20 border-destructive/50", text: "text-destructive" },
  WARNING: { bg: "bg-warning/20 border-warning/50", text: "text-warning" },
  INFO: { bg: "bg-primary/20 border-primary/50", text: "text-primary" },
};

export const AlertProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [prices, setPrices] = useState({});
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const retriesRef = useRef(0);
  const MAX_RETRIES = 5;

  const connectWebSocket = useCallback(() => {
    if (!token || wsRef.current?.readyState === WebSocket.OPEN) return;
    if (retriesRef.current >= MAX_RETRIES) return;

    try {
      const ws = new WebSocket(`${WS_BASE}/api/ws/${token}`);
      wsRef.current = ws;

      ws.onopen = () => {
        setWsConnected(true);
        retriesRef.current = 0;
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "ping" }));
          }
        }, 25000);
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === "prices") {
            setPrices(msg.data);
          } else if (msg.type === "alert") {
            const alert = msg.data;
            setAlerts((prev) => [alert, ...prev.slice(0, 99)]);
            setUnreadCount((prev) => prev + 1);
            const style = SEVERITY_STYLES[alert.severity] || SEVERITY_STYLES.INFO;
            toast[alert.severity === "CRITICAL" ? "error" : alert.severity === "WARNING" ? "warning" : "info"](
              alert.title,
              { description: alert.message, duration: alert.severity === "CRITICAL" ? 10000 : 5000 }
            );
          }
        } catch (e) {
          // ignore parse errors
        }
      };

      ws.onclose = () => {
        setWsConnected(false);
        clearInterval(pingIntervalRef.current);
        retriesRef.current += 1;
        if (retriesRef.current < MAX_RETRIES) {
          const delay = Math.min(30000, 5000 * Math.pow(2, retriesRef.current));
          reconnectTimerRef.current = setTimeout(connectWebSocket, delay);
        }
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch (e) {
      retriesRef.current += 1;
    }
  }, [token]);

  // Connect on login
  useEffect(() => {
    if (token) {
      connectWebSocket();
      fetchUnreadCount();
    }
    return () => {
      clearInterval(pingIntervalRef.current);
      clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [token, connectWebSocket]);

  const fetchUnreadCount = async () => {
    try {
      const res = await axios.get(`${API}/alerts/unread-count`);
      setUnreadCount(res.data.count);
    } catch (e) {
      // silent
    }
  };

  const fetchAlerts = async (limit = 50, unreadOnly = false) => {
    try {
      const res = await axios.get(`${API}/alerts`, {
        params: { limit, unread_only: unreadOnly },
      });
      setAlerts(res.data);
      return res.data;
    } catch (e) {
      return [];
    }
  };

  const markRead = async (alertId) => {
    try {
      await axios.put(`${API}/alerts/${alertId}/read`);
      setAlerts((prev) => prev.map((a) => (a.id === alertId ? { ...a, read: true } : a)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (e) {
      // silent
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/alerts/read-all`);
      setAlerts((prev) => prev.map((a) => ({ ...a, read: true })));
      setUnreadCount(0);
    } catch (e) {
      // silent
    }
  };

  return (
    <AlertContext.Provider
      value={{
        alerts,
        unreadCount,
        prices,
        wsConnected,
        fetchAlerts,
        markRead,
        markAllRead,
        fetchUnreadCount,
      }}
    >
      {children}
    </AlertContext.Provider>
  );
};

export const useAlerts = () => {
  const ctx = useContext(AlertContext);
  if (!ctx) throw new Error("useAlerts must be used within AlertProvider");
  return ctx;
};
